import { Card, IconButton, Typography, Grid, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Divider } from "@mui/material";
import MealCard from "./meal-card";
import { useState } from "react";
import { MealSnapshot, NutritionPlan } from "../../../../service/model/nutrition.model";
import { AddIcon } from "../../../../shared/icons";
import useMealItems from "../../../../hooks/use-meal-items";
import useUnits from "../../../../hooks/use-units";
import StyledChip from "../../../../components/styled-chip";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { selectActivePlan, updateActivePlan } from "../nutrition-plan.slice";
import theme from "../../../../app/app-theme";
import { getTotalDayNutrition, getTotalMealNutrition, getTotalPlanNutrition } from "../../../../shared/utils";

interface MealsProps {
    dayIndex: number;
}

const Meals: React.FC<MealsProps> = ({ dayIndex }) => {
    const dispatch = useAppDispatch();
    const activePlan = useAppSelector(selectActivePlan);
    const meals = activePlan?.planSnapshot?.days?.[dayIndex]?.meals || [];
    const [mealItems] = useMealItems();

    // dialog state
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [newMealName, setNewMealName] = useState("");

    const handleAddMeal = (mealName: string) => {
        const newMeal: MealSnapshot = {
            index: meals.length,
            mealTime: mealName,
            items: [],
            totalMealNutrition: {
                calories: 0,
                protein: 0,
                carbohydrates: 0,
                fat: 0,
                fiber: 0,
                sugar: 0,
            }
        };
        dispatch(updateActivePlan({
            ...activePlan,
            planSnapshot: {
                ...activePlan?.planSnapshot,
                days: activePlan?.planSnapshot?.days?.map((day, index) =>
                    index === dayIndex
                        ? { ...day, meals: [...(day.meals || []), newMeal] }
                        : day
                )
            }
        } as NutritionPlan));
    };

    const openDialog = () => {
        setNewMealName("");
        setIsDialogOpen(true);
    };

    const closeDialog = () => {
        setIsDialogOpen(false);
    };

    const confirmAddMeal = () => {
        if (newMealName.trim()) {
            handleAddMeal(newMealName.trim());
        }
        closeDialog();
    };


    return (
        <>
            {/* <Typography variant="h5">Meals</Typography> */}
            <Grid container direction="row" justifyContent="flex-start" spacing={2}>

                {meals.map((meal) => (
                    <Grid size={{ xs: 12 }} sx={{ display: "flex", flexDirection: "column" }} key={meal.index}>
                        <MealCard
                            key={`${meal.index}-${meal.mealTime}`}
                            meal={meal}
                            mealItems={mealItems}
                            onDelete={() => {
                                dispatch(updateActivePlan({
                                    ...activePlan,
                                    planSnapshot: {
                                        ...activePlan?.planSnapshot,
                                        days: activePlan?.planSnapshot?.days.map((day, dayIdx) =>
                                            dayIdx === dayIndex
                                                ? { ...day, meals: day.meals.filter(m => m.index !== meal.index) }
                                                : day
                                        )
                                    }
                                } as NutritionPlan));
                            }}
                            onMealItemsChange={(index, mealItems) => {
                                dispatch(updateActivePlan({
                                    ...activePlan,
                                    planSnapshot: {
                                        ...activePlan?.planSnapshot,
                                        days: activePlan?.planSnapshot?.days.map((day, dayIdx) =>
                                            dayIdx === dayIndex
                                                ? {
                                                    ...day,
                                                    meals: day.meals.map((m, i) => i === index ? {
                                                        ...m,
                                                        items: mealItems,
                                                        totalMealNutrition: getTotalMealNutrition(mealItems)
                                                    } : m
                                                    ),
                                                    totalDayNutrition: getTotalDayNutrition(day.meals.map((m, i) => i === index ? { ...m, items: mealItems } : m))
                                                }
                                                : day
                                        ),                                        
                                        totalPlanNutrition: getTotalPlanNutrition(activePlan?.planSnapshot?.days ?? [])
                                    }
                                } as NutritionPlan));
                            }}
                        />
                    </Grid>
                ))}
                <Grid size={{ xs: 12, sm: 12, md: 6, lg: 3 }}>
                    <Card sx={{ p: 2, mb: 2, display: "flex", justifyContent: "center", alignItems: "center" }}>
                        <IconButton onClick={openDialog}>
                            <AddIcon fontSize="large" />
                        </IconButton>
                        <Typography variant="body2">Add Meal</Typography>
                    </Card>
                </Grid>
            </Grid>

            <Dialog open={isDialogOpen} onClose={closeDialog}>
                <DialogTitle>Add Meal</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Meal name"
                        fullWidth
                        value={newMealName}
                        onChange={(e) => setNewMealName(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') confirmAddMeal();
                        }}
                    />

                    <Divider sx={{ my: 2 }} />
                    <Typography variant="body2" color="textSecondary">
                        Please enter a name for the meal or select frequently used one from below list.
                    </Typography>
                    {["Breakfast", "Lunch", "Dinner", "Snack", "Pre-Workout", "Post-Workout"].map((mealName) => (
                        <StyledChip
                            key={mealName}
                            onClick={() => {
                                setNewMealName(mealName);
                            }}
                            label={mealName}
                            sx={{ mt: 1, mr: 1, cursor: "pointer" }}
                        />
                    ))}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDialog} variant="contained" sx={{ bgcolor: `${theme.palette.secondary.main}` }}>Cancel</Button>
                    <Button onClick={confirmAddMeal} variant="contained" sx={{ bgcolor: `${theme.palette.primary.main}` }}>Save</Button>
                </DialogActions>
            </Dialog>
        </>

    );
};

export default Meals;
